import React from "react";
import ReactSelect, { GroupBase, components } from "react-select";
import { ISelect, ISelectOption } from "types/gaiaComponents/select";
import type { ISelectDropdownOption } from "types/gaiaComponents/select";
import { CheckIcon, CircleXIcon, Search } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Loader, LoaderSize, LoaderType } from "components/common/Loader.component";
import { getGaiaStyles } from "./Select.styles";
import "scss/migration_gaia.scss";

// Re-export types for convenience
export type { ISelectOption, ISelectDropdownOption };

// Custom Option component with CheckIcon for selected items (matching DropdownListItem)
const CustomOption = (props: any) => {
    return (
        <components.Option {...props}>
            <div className="d-flex gap-2 align-items-center w-100">
                {props.isSelected ? (
                    <CheckIcon size={ICON_SIZE.SMALL} />
                ) : (
                    <div className="d-inline-block ga-select__option-spacer" /> // Spacer to maintain alignment
                )}
                <span className="flex-grow-1">{props.label}</span>
                {props.data.shortKey && (
                    <span className="ms-auto text-muted small">
                        {props.data.shortKey}
                    </span>
                )}
            </div>
        </components.Option>
    );
};

// Helper function to normalize option to get label
const getOptionLabel = (option: any): string => {
    if (typeof option === 'string') return option;
    if (option.label) return option.label;
    if (option.text) return option.text;
    return String(option.value || '');
};

// Helper function to check if option is disabled
const isOptionDisabled = (option: any): boolean => {
    return option.isDisabled || option.disabled || false;
};


// Custom ClearIndicator with CircleXIcon (matching DropdownSelect)
const ClearIndicator = (props: any) => {
    const {
        clearValue,
        translatedClearSearch,
    } = props;

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        clearValue();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            clearValue();
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <button
            type="button"
            className="d-flex align-items-center ga-select__clear-button"
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            aria-label={translatedClearSearch}
        >
            <CircleXIcon size={ICON_SIZE.SMALL} className="ga-icon ga-select__action-icon"/>
        </button>
    );
};

// Custom LoadingIndicator component using Loader
const LoadingIndicator = () => {
    return (
        <div className="d-flex align-items-center justify-content-center px-2">
            <Loader
                type={LoaderType.Default}
                size={LoaderSize.Small}
            />
        </div>
    );
};

// Custom IndicatorsContainer to control the order of indicators
const IndicatorsContainer = (props: any) => {
    const { selectProps, hasValue } = props;
    const showLoadingIndicator = selectProps.isLoading;
    const showClearIndicator = selectProps.isClearable && hasValue;

    return (
        <div className="d-flex align-items-center">
            {showLoadingIndicator && <LoadingIndicator />}
            {showClearIndicator && <ClearIndicator {...props} />}
        </div>
    );
};

// Custom Control component with Search icon
const Control = (props: any) => {
    return (
        <components.Control {...props}>
            <div className="d-flex align-items-center gap-2">
                <Search size={ICON_SIZE.MEDIUM}/>
            </div>
            {props.children}
        </components.Control>
    );
};

// Create custom loading message component
const LoadingMessageComponent: React.FC<{ loadingText: string }> = ({ loadingText }) => {
    const [showText, setShowText] = React.useState(false);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setShowText(true);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="d-flex align-items-center justify-content-center ga-select__loading-message">
            <Loader
                type={LoaderType.Default}
                size={LoaderSize.Small}
                text={showText ? loadingText : undefined}
            />
        </div>
    );
};

const LoadingMessage = (loadingText: string) => {
    return () => <LoadingMessageComponent loadingText={loadingText} />;
};

// Create custom no options message component
const NoOptionsMessage = (noResultsHeader: string, noResultsText: string) => {
    return () => {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center px-2 py-4 gap-2 ga-select__no-options">
                <p className="ga-text-md text-action my-0">{noResultsHeader}</p>
                {noResultsText && <p className="ga-text-xs text-action my-0">{noResultsText}</p>}
            </div>
        );
    };
};

export const Select = React.forwardRef(<
    Option = ISelectOption,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(props: ISelect<Option, IsMulti, Group>, ref: React.Ref<any>) => {
    const {
        id,
        options,
        value,
        defaultValue,
        placeholder,
        isDisabled = false,
        isSearchable = true,
        isClearable = false,
        isMulti = false as IsMulti,
        isLoading = false,
        invalid = false,
        showNoOptions = true,
        tabIndex,
        className,
        onChange,
        onBlur,
        onFocus,
        onInputChange,
        onMenuOpen,
        onMenuClose,
        onBlurResetsInput,
        onCloseResetsInput,
        autoFocus,
        tabSelectsValue,
        onMenuScrollToBottom,
        closeMenuOnSelect,
        dataCy,
        formatOptionLabel,
        filterOption,
        menuPortalTarget,
        menuPosition = 'absolute',
        defaultMenuIsOpen,
        maxMenuHeight,
        menuShouldScrollIntoView,
        blurInputOnSelect,
        menuPlacement,
        translate,
    } = props;

    // Translate messages using the translate function from props
    const translatedLoadingMessage = translate("select.stillSearching");
    const translatedNoResultsHeader = translate('select.noResults');
    const translatedNoResultsText = translate('select.adjustFilters');
    const translatedPlaceholder = placeholder || translate("select.search");
    const translatedClearSearch = translate("select.clearSearch");

    const gaiaStyles = getGaiaStyles<Option, IsMulti, Group>(invalid);

    // Merge custom components with default components
    const defaultComponents = {
        IndicatorSeparator: () => null,
        Option: CustomOption,
        DropdownIndicator: () => null,
        IndicatorsContainer: (props) =>IndicatorsContainer({...props, translatedClearSearch}),
        Control: Control,
        LoadingMessage: LoadingMessage(translatedLoadingMessage),
        ...(showNoOptions && {
            NoOptionsMessage: NoOptionsMessage(translatedNoResultsHeader, translatedNoResultsText),
        }),
    };

    // Build className without nested template literals
    const classNames = [
        'ga-select-container',
        className,
        invalid && 'ga-select--invalid'
    ].filter(Boolean).join(' ');

    return (
        <div data-cy={dataCy}>
            <ReactSelect<Option, IsMulti, Group>
                ref={ref}
                inputId={id}
                options={options}
                value={value}
                defaultValue={defaultValue}
                tabIndex={tabIndex}
                placeholder={translatedPlaceholder}
                isDisabled={isDisabled}
                isSearchable={isSearchable}
                isClearable={isClearable}
                isMulti={isMulti}
                isLoading={isLoading}
                className={classNames}
                classNamePrefix="ga-select"
                styles={gaiaStyles}
                components={defaultComponents}
                getOptionLabel={getOptionLabel}
                isOptionDisabled={isOptionDisabled}
                onChange={onChange as any}
                onBlur={onBlur}
                onFocus={onFocus}
                onInputChange={onInputChange}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                onBlurResetsInput={onBlurResetsInput}
                onCloseResetsInput={onCloseResetsInput}
                autoFocus={autoFocus}
                tabSelectsValue={tabSelectsValue}
                onMenuScrollToBottom={onMenuScrollToBottom}
                closeMenuOnSelect={closeMenuOnSelect}
                formatOptionLabel={formatOptionLabel}
                filterOption={filterOption}
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                menuPlacement={menuPlacement}
                defaultMenuIsOpen={defaultMenuIsOpen}
                maxMenuHeight={maxMenuHeight}
                menuShouldScrollIntoView={menuShouldScrollIntoView}
                noOptionsMessage={showNoOptions ? undefined : () => null} // Conditionally show no options message
                blurInputOnSelect={blurInputOnSelect}
            />
        </div>
    );
}) as <
    Option = ISelectOption,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(
    props: ISelect<Option, IsMulti, Group> & { ref?: React.Ref<any> }
) => React.ReactElement;

export default Select;
